import React, { useState, useEffect } from "react";
import "./cashflow.less";
import service from "../../../../services/service";
import Assumption from "../Assumption/assumption";
import {
  formatNumber,
  formatListingDate,
} from "../../../../utils/financialHelper";
import {
  ReitsValuationContextProvider,
  useReitsValuationState,
} from "../../../../context/reitsValuationContext";
import {
  initiateCashflowData,
  updateCalculatedValues,
} from "../../../../services/cashflow-service";
import {
  calculateXNPV,
  initialcalculateWACC,
} from "../../../../services/wacc-service";

interface CashflowProps {
  locations: string[];
  propertiesByLocation: Record<string, string[]>;
}

export default function Cashflow({
  locations,
  propertiesByLocation,
}: CashflowProps) {
  const heading = "Valuation at property level using";
  const subheading = "10-Year Discounted Cash Flow Method";

  const { evalState, setEvalState } = useReitsValuationState();

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [dateOptions, setDateOptions] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [valuationData, setValuationData] = useState<any>({});
  const [propertyOptions, setPropertyOptions] = useState<string[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [basedReport, setbasedReport] = useState(null);
  const [basedReportValue, setbasedReportValue] = useState(null);

  useEffect(() => {
    if (locations.length > 0) {
      const firstLocation = locations[0];
      setSelectedLocation(firstLocation);

      const propsForLocation = propertiesByLocation[firstLocation] || [];
      setPropertyOptions(propsForLocation);

      if (propsForLocation.length > 0) {
        const firstProperty = propsForLocation[0];
        setSelectedProperty(firstProperty);
      }
    }
  }, [locations, propertiesByLocation]);

  useEffect(() => {
    if (selectedLocation) {
      const propsForLocation = propertiesByLocation[selectedLocation] || [];
      setPropertyOptions(propsForLocation);

      if (!propsForLocation.includes(selectedProperty)) {
        const firstProperty = propsForLocation[0] || "";
        setSelectedProperty(firstProperty);
      }
    }
  }, [selectedLocation, propertiesByLocation]);

  // 🔹 Fetch valuation data when property changes
  useEffect(() => {
    const fetchValuation = async () => {
      if (selectedProperty) {
        try {
          const valuation = await service.valuationAdjacent(selectedProperty);
          if (valuation) {
            setValuationData(valuation);
            setbasedReport(valuation.basedReport);
            setbasedReportValue(valuation.basedReportValue);

            if (valuation?.historicalData?.length) {
              const dates = valuation.historicalData.map((d: any) => d.date);
              setDateOptions(dates);
              const firstDate = dates[0];
              setSelectedDate(firstDate);

              // 🔹 Initialize evalState for Assumption modal
              const initialData = valuation.historicalData[0];

              const cashFlowInit = initiateCashflowData(
                new Date(firstDate),
                parseFloat(initialData.leasableArea) * 1000000,
                valuation.occupancy,
                valuation.currentRent,
                evalState.noiPercentage
              );

              const updatedCashFlow = updateCalculatedValues(
                cashFlowInit.cashFlowData,
                cashFlowInit.columns,
                evalState.noiPercentage,
                false,
                valuation.capRate
              );

              const initialWACC = initialcalculateWACC(
                valuation.location,
                valuation.wale,
                valuation.esgCompliance,
                valuation.buildingAge,
                valuation.vacancy
              );

              const effectiveDiscountRate =
                initialWACC.WACCResults.waccDiscountRate +
                initialWACC.WACCResults.totalRiskPremium;

              const valuationCare = parseFloat(
                calculateXNPV(
                  initialWACC.WACCResults.waccDiscountRate,
                  cashFlowInit.cashFlowData[5].values.slice(1),
                  cashFlowInit.columns
                ).toFixed(2)
              );

              setEvalState((prev) => ({
                ...prev,
                valuationDate: firstDate,
                leaseableArea: parseFloat(initialData.leasableArea),
                occupancy: valuation.occupancy,
                avgRental: valuation.currentRent,
                capRate: valuation.capRate,
                columns: cashFlowInit.columns,
                cashFlowData: updatedCashFlow,

                // WACC
                costOfEquity: initialWACC.waccRange.costOfEquity,
                costOfDebt: initialWACC.waccRange.costOfDebt,
                weightOfDebt: initialWACC.waccRange.weightOfDebt,
                weightOfEquity: initialWACC.waccRange.weightOfEquity,
                waccRange: initialWACC.WACCResults.calculatedWacc,

                // Risk Premium
                location: valuation.location,
                tenantProfile: initialWACC.riskPremiums.tenantProfile,
                wale: initialWACC.riskPremiums.wale,
                buildingLife: initialWACC.riskPremiums.buildingLife,
                vacancy: initialWACC.riskPremiums.vacancy,
                propertyType: initialWACC.riskPremiums.propertyType,
                esgCompliance: initialWACC.riskPremiums.esgCompliance,
                riskPremium: initialWACC.WACCResults.totalRiskPremium,

                // Valuations
                waccDiscountRate: effectiveDiscountRate,
                waccDiscountRateUser: effectiveDiscountRate,
                valuationUser: valuationCare,
                valuationCare: valuationCare,
              }));
            }

            if (valuation?.historicalData?.length) {
              const dates = valuation.historicalData.map((d: any) => d.date);
              setDateOptions(dates);
              setSelectedDate(dates[0]);
            } else {
              setDateOptions([]);
              setSelectedDate("");
            }
          }
        } catch (err) {
          console.error("Error fetching valuation data:", err);
        }
      }
    };
    fetchValuation();
  }, [selectedProperty]);

  const cards = [
    {
      title: "CareEdge Standard Valuation Model",
      rate: `${evalState.waccDiscountRate.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
      rateLabel: "Effective Discount Rate",
      valuation: `${Number(evalState.valuationCare.toFixed(2)).toLocaleString(
        "en-IN"
      )}`,
      valuationLabel: "Valuation (Rs. Crore)",
    },
    {
      title: "Based on Users Assumption",
      rate: `${evalState.waccDiscountRateUser.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
      rateLabel: "Effective Discount Rate",
      valuation: `${Number(evalState.valuationUser.toFixed(2)).toLocaleString(
        "en-IN"
      )}`,
      valuationLabel: "Valuation (Rs. Crore)",
      hasPopup: true,
    },
    {
      title: "Based on Market Report",
      rate:
        basedReport !== null
          ? `${Number(basedReport).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}%`
          : "-",
      rateLabel: "Effective Discount Rate",

      valuation:
        basedReportValue !== null
          ? Number(Number(basedReportValue).toFixed(2)).toLocaleString("en-IN")
          : "-",
      valuationLabel: "Valuation (Rs. Crore)",
    },
  ];

  const editIcon = new URL("../../../../assets/editIcon.png", import.meta.url)
    .href;

  // 🔹 Define table fields with custom labels
  const tableFields = [
    { label: "Location", key: "location" },
    { label: "Construction Status", key: "constructionStatus" },
    {
      label: "Leasable Area (in msf/ Hotel keys in nos./ MW)",
      key: "leasableArea",
    },
    {
      label: "Leased Area (in msf/ Hotel keys in nos./ MW)",
      key: "leasedArea",
    },
    { label: "Occupancy (%)", key: "occupancy" },
    { label: "Vacancy (%)", key: "vacancy" },
    { label: "Effective current rent (Rs/sq.ft/month)", key: "currentRent" },
    { label: "Market rent (Rs/sq.ft/month)", key: "marketRent" },
    { label: "WALE (years)", key: "wale" },
  ];

  return (
    <section className="cashflow-section">
      <div className="cashflow">
        {/* Top Section */}
        <div className="cashflow__top">
          <div className="cashflow__heading">
            <h3>
              {heading} <br />
              <span>{subheading}</span>
            </h3>
          </div>

          <div className="cashflow__filters">
            <select
              className="filter-select"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map((loc, i) => (
                <option key={i} value={loc}>
                  {loc}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
            >
              {propertyOptions.map((prop, i) => (
                <option key={i} value={prop}>
                  {prop}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              {dateOptions.map((date, i) => (
                <option key={i} value={date}>
                  {formatListingDate(date)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="cashflow__bottom">
          {/* Left Cards */}
          <div className="cashflow__cards">
            {cards.map((card, idx) => (
              <div key={idx} className="cashflow__card">
                <div className="cashflow__card-header">
                  <p className="cashflow__card-title">{card.title}</p>
                  {card.hasPopup && (
                    <img
                      src={editIcon}
                      alt="Edit"
                      className="cashflow__card-popup"
                      width={22}
                      height={22}
                      onClick={() => setShowPopup(true)}
                    />
                  )}
                  {showPopup && (
                    <Assumption onClose={() => setShowPopup(false)} selectedLocation={selectedLocation}/>
                  )}
                </div>

                <div className="cashflow__card-body">
                  <div className="cashflow__card-col">
                    <h1 className="cashflow__card-value">{card.rate}</h1>
                    <span className="cashflow__card-label">
                      {card.rateLabel}
                    </span>
                  </div>
                  <div className="cashflow__card-col">
                    <h1 className="cashflow__card-value">{card.valuation}</h1>
                    <span className="cashflow__card-label">
                      {card.valuationLabel}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Table */}
          <div className="cashflow__table">
            <table>
              <thead>
                <tr>
                  <th>Name of the Asset</th>
                  <th>{valuationData.assetName || "-"}</th>
                </tr>
              </thead>
              <tbody>
                {tableFields.map((field) => (
                  <tr key={field.key}>
                    <td>{field.label}</td>
                    <td>
                      {(() => {
                        // Handle leasableArea from historicalData, everything else from top-level
                        const val =
                          field.key === "leasableArea"
                            ? valuationData.historicalData?.[0]?.leasableArea ??
                              null
                            : valuationData[field.key];

                        if (val === null || val === undefined || val === "")
                          return "-";

                        // If number or numeric string → format with formatNumber
                        if (!isNaN(Number(val))) {
                          return formatNumber(
                            Number(val),
                            field.key === "occupancy" || field.key === "vacancy"
                          );
                        }

                        // Otherwise, show string as-is
                        return val;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="cashflow__disclaimer">
          <h4>
            Disclaimer : Methodology - 10 - Year Discounted Cash Flow Method
          </h4>
          <p>
            The income approach is based on the premise that value of an
            income-producing asset is a function of future benefits and income
            derived from that asset.
          </p>
          <p>
            Net operating income (NOI) has primarily been used to arrive at the
            value of the commercial office assets. The projected future cash
            flows from the property are based on existing lease terms for the
            operational area till the expiry of the leases or re-negotiation,
            whichever is earlier. These cash flows have been projected for
            10-year duration from the date of valuation and 10th year NOI is
            used for assessment of terminal value. NOI of 10th year is
            capitalised at an appropriate rate to arrive at the terminal value.
            These future cash flows and terminal value are then discounted to
            present-day value (valuation date) at an appropriate discount rate.
          </p>
          <p>
            Note: This valuation model is exclusively designed for completed
            commercial office and retail spaces. It is not applicable to
            properties under construction. It may also be noted that there may
            be a 10-15% variance between published valuations and the results
            generated by the model and the variation could be higher for the
            properties where the occupancy rates are below 80%.
          </p>
        </div>
      </div>
    </section>
  );
}
